import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import EmptyState from "../../../components/Common/EmptyState"
import ProjectSessionDetail from "../../../components/DashboardComponents/ProjectSessionDetail"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIRECTION,
  MEMBERS_PAGE_SIZE,
  mapSessionCollaborator,
  mapSessionDetail,
  matchesSessionCollabStatusFilters,
} from "../../../helpers/sessionDetail"
import { ADMIN_SESSION_DETAIL_WITH } from "./adminSessionsData"

const BACK_PATH = "/admin/sessions"

/**
 * Admin Sessions — read-only session detail (no timer / exit / collab edits).
 * GET /api/v1/admin/sessions/:id + collaborators list.
 */
const AdminSessionDetail = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()

  const [session, setSession] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [members, setMembers] = useState([])
  const [membersTotal, setMembersTotal] = useState(0)
  const [membersPage, setMembersPage] = useState(1)
  const [membersPageSize, setMembersPageSize] = useState(MEMBERS_PAGE_SIZE)
  const [isMembersLoading, setIsMembersLoading] = useState(true)
  const [membersError, setMembersError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilters, setStatusFilters] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION)

  useEffect(() => {
    document.title = "Session Detail | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    setMembersPage(1)
  }, [debouncedSearch, statusFilters, sortBy, sortDirection])

  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setSession(null)
      setError("Session not found")
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await get(endpoints.getAdminSessionById(sessionId), {
        params: { "with[]": ADMIN_SESSION_DETAIL_WITH },
      })

      if (response?.data?.success) {
        const mapped = mapSessionDetail(response.data.result || {})
        setSession({
          ...mapped,
          canExit: false,
          canToggleTimer: false,
          canManageCollaborators: false,
          isRunning: false,
          isOnBreak: false,
          timerStatus: mapped.timerStatus || "ended",
        })
      } else {
        const message = toastApiError(response, "Failed to load session")
        setError(message)
        setSession(null)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load session")
      setError(message)
      setSession(null)
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  const fetchMembers = useCallback(async () => {
    if (!sessionId) {
      setMembers([])
      setMembersTotal(0)
      setIsMembersLoading(false)
      return
    }

    setIsMembersLoading(true)
    setMembersError(null)

    try {
      const params = {
        "with[]": ["user"],
        page: membersPage,
        per_page: MEMBERS_PAGE_SIZE,
        page_size: MEMBERS_PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }

      if (debouncedSearch) params.search = debouncedSearch

      if (statusFilters) {
        params.page = 1
        params.per_page = Math.max(MEMBERS_PAGE_SIZE, 100)
        params.page_size = params.per_page
        params.status = statusFilters
      }

      const response = await get(
        endpoints.getAdminSessionCollaborators(sessionId),
        { params },
      )

      if (response?.data?.success) {
        const result = response.data.result || {}
        let rows = (result.data || []).map(mapSessionCollaborator)

        if (statusFilters) {
          rows = rows.filter(row =>
            matchesSessionCollabStatusFilters(row, statusFilters),
          )
        }

        const total = statusFilters
          ? rows.length
          : (result.total ?? rows.length)
        const perPage =
          result.per_page || result.page_size || MEMBERS_PAGE_SIZE

        setMembers(rows)
        setMembersTotal(total)
        setMembersPageSize(perPage)

        const totalPages = Math.max(1, Math.ceil(total / perPage))
        if (membersPage > totalPages) setMembersPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load collaborators")
        setMembersError(message)
        setMembers([])
        setMembersTotal(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load collaborators")
      setMembersError(message)
      setMembers([])
      setMembersTotal(0)
    } finally {
      setIsMembersLoading(false)
    }
  }, [
    sessionId,
    membersPage,
    debouncedSearch,
    statusFilters,
    sortBy,
    sortDirection,
  ])

  useEffect(() => {
    fetchSession()
  }, [fetchSession])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const gallery = useMemo(
    () =>
      members.map(member => ({
        id: member.id,
        name: member.name,
        src: member.avatar,
        status: member.galleryStatus,
      })),
    [members],
  )

  const goBack = () => navigate(BACK_PATH)

  if (!isLoading && !session) {
    return (
      <div className="admin-page admin-session-detail">
        <button
          type="button"
          className="admin-session-detail__back"
          onClick={goBack}
        >
          <img src={ICONS.arrowLeft} alt="" width={16} height={16} />
          Back to Sessions
        </button>
        <EmptyState
          title="Session not found"
          description={error || "This session could not be loaded."}
          actionText="Back to Sessions"
          onActionClick={goBack}
        />
      </div>
    )
  }

  return (
    <div className="admin-page admin-session-detail">
      <button
        type="button"
        className="admin-session-detail__back"
        onClick={goBack}
      >
        <img src={ICONS.arrowLeft} alt="" width={16} height={16} />
        Back to Sessions
      </button>

      <ProjectSessionDetail
        session={session}
        timer={session?.timer}
        isLoading={isLoading}
        error={error}
        members={members}
        gallery={gallery}
        membersTotal={membersTotal}
        membersPage={membersPage}
        membersPageSize={membersPageSize}
        isMembersLoading={isMembersLoading}
        membersError={membersError}
        onMembersPageChange={setMembersPage}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilters={statusFilters}
        onStatusFiltersChange={setStatusFilters}
        sortBy={sortBy}
        onSortByChange={next => {
          setSortBy(next || DEFAULT_SORT_BY)
          setMembersPage(1)
        }}
        sortDirection={sortDirection}
        onSortDirectionChange={next => {
          setSortDirection(next || DEFAULT_SORT_DIRECTION)
          setMembersPage(1)
        }}
        alwaysShowClose
        onClose={goBack}
        project={{ is_current_user_active_member: false }}
      />
    </div>
  )
}

export default AdminSessionDetail
